// Every English string on the catering booking screens (Stage 2B), in one
// place for a later Urdu table.
import type { BookingAction, BookingStatus, PaymentKind, PaymentMethod } from './cateringTypes'

export const CT = {
  title: 'Catering',
  subtitle: 'Events booked ahead — dishes, money, and the kitchen plan.',
  newBooking: 'New booking',
  filters: { upcoming: 'Upcoming', unpaid: 'Unpaid', past: 'Past', cancelled: 'Cancelled' },
  noBookings: 'No bookings here.',
  loadFailed: 'Could not load the bookings — the connection may be down.',
  back: 'Back to bookings',
  balanceDue: (amt: string) => `${amt} due`,
  paidUp: 'Paid',
  owedBack: (amt: string) => `${amt} to refund`,
  guests: (n: number) => `${n} guest${n === 1 ? '' : 's'}`,
  // editor
  customer: 'Customer name',
  phone: 'Phone (optional)',
  eventName: 'Event (optional)',
  eventNamePlaceholder: 'Walima, office lunch…',
  eventDate: 'Event date',
  eventTime: 'Time (optional)',
  venue: 'Venue (optional)',
  guestCount: 'Guests (optional)',
  served: 'How is it served?',
  dineIn: 'Served at the venue',
  takeaway: 'Packed to go',
  dishes: 'Dishes',
  searchDishes: 'Search the menu…',
  noDishes: 'No dishes match.',
  addDishHint: 'Tap a dish to add it. The menu price is used unless you set a special price.',
  menuPrice: 'Menu',
  specialPrice: 'Special price',
  lineNote: 'Note',
  remove: 'Remove',
  discount: 'Discount',
  discountNote: 'Why the discount? (optional)',
  notes: 'Notes (optional)',
  subtotal: 'Subtotal',
  total: 'Total',
  paid: 'Paid',
  balance: 'Balance',
  save: 'Save booking',
  saveConfirm: 'Save and confirm',
  saving: 'Saving…',
  saved: 'Booking saved',
  discountTooBig: 'The discount is more than the food total.',
  needCustomer: 'Enter the customer’s name.',
  needDate: 'Pick the event date.',
  needDish: 'Add at least one dish.',
  needQty: 'Enter how many for every dish, or remove it.',
  needGuests: 'Enter the number of guests.',
  needPricePerGuest: 'Enter the price per guest.',
  enterHowMany: 'Enter how many',
  portionHint: (guests: number, lines: { name: string; qty: number }[]) =>
    [`Guests ${guests}`, ...lines.map((l) => `${l.name} is for ${l.qty}`)].join(' · '),
  discardTitle: 'Discard your changes?',
  discardBody: 'What you changed on this booking will be lost.',
  discard: 'Discard changes',
  keepEditing: 'Keep editing',
  // detail
  edit: 'Change booking',
  dishesLocked: 'Dishes are locked once the event is delivered. Prices and the discount can still change.',
  readOnly: 'You can see this booking but not change it.',
  revision: (n: number) => `revision ${n}`,
  payments: 'Payments',
  noPayments: 'No payments yet.',
  addPayment: 'Take a payment',
  kind: 'Type',
  method: 'Method',
  amount: 'Amount',
  reference: 'Reference (optional)',
  referenceHint: 'Bank transfer ID, card slip no…',
  paymentNote: 'Note (optional)',
  record: 'Record',
  receivedOn: 'Received on',
  amountInvalid: 'Enter the amount as a number, like 10,000.',
  receivedFuture: 'The date can’t be in the future.',
  recorded: 'Payment recorded',
  voidIt: 'Void',
  voidReason: 'Why void it?',
  voidConfirm: 'Void payment',
  voided: 'Voided',
  voidedOk: 'Payment voided',
  refundMax: (amt: string) => `At most ${amt} can be refunded.`,
  invoice: 'Invoice PDF',
  noBusinessName: 'No business name is set, so invoices print without one.',
  setBusinessName: 'Set it in Settings',
  receipt: 'Receipt PDF',
  kitchen: 'Kitchen',
  sendToKitchen: 'Send to kitchen',
  sentToKitchen: (no: string) => `Sent to the kitchen as ${no}`,
  kitchenPlan: (no: string, status: string) => `Kitchen plan ${no} — ${status}`,
  openPlan: 'Open plan',
  confirmFirst: 'Confirm the booking before sending it to the kitchen.',
  notSentYet: 'Not with the kitchen yet.',
  kitchenModes: {
    updated: 'Kitchen plan updated.',
    reopened: 'Kitchen plan opened again for the chef to lock.',
    revised: 'Kitchen plan recalculated.',
  },
  topUp: (no: string) => `Top-up request ${no} sent to the store.`,
  lessNeeded: 'Less is needed now — the kitchen decides what to keep or send back.',
  history: 'History',
  cancelReason: 'Why cancel? (optional)',
  cancelConfirm: 'Yes, cancel booking',
  cancelTitle: 'Cancel this booking?',
  deliverTitle: 'Mark this booking delivered?',
  deliverBody: 'The dishes are locked after this.',
  deliverBodyNoPlan: 'It has not been sent to the kitchen. Once delivered, the dishes are locked and it can’t be sent to the kitchen any more.',
  deliverEarly: (date: string) => `The event is on ${date} — mark it delivered anyway?`,
  closeTitle: 'Close this booking?',
  closeBody: 'A closed booking can’t be changed and takes no more payments.',
  keep: 'Keep it',
  notYet: 'Not yet',
  closeHelp: 'Close once the event is delivered and fully paid.',
  catering: 'Catering money is kept apart from the till and Daily Closing.',
  // payments report
  report: 'Payments',
  reportTitle: 'Catering payments',
  received: 'Received',
  refunded: 'Refunded',
  net: 'Net',
  from: 'From',
  to: 'To',
  booking: 'Booking',
  when: 'When',
  status: {
    enquiry: 'Enquiry',
    confirmed: 'Confirmed',
    delivered: 'Delivered',
    closed: 'Closed',
    cancelled: 'Cancelled',
  } satisfies Record<BookingStatus, string>,
  kinds: { deposit: 'Deposit', payment: 'Payment', refund: 'Refund' } satisfies Record<PaymentKind, string>,
  methods: { cash: 'Cash', bank_transfer: 'Bank transfer', card: 'Card' } satisfies Record<PaymentMethod, string>,
  actions: {
    confirm: 'Confirm booking',
    deliver: 'Mark delivered',
    close: 'Close booking',
    cancel: 'Cancel booking',
  } satisfies Record<BookingAction, string>,
  actionDone: {
    confirm: 'Booking confirmed',
    deliver: 'Marked delivered',
    close: 'Booking closed',
    cancel: 'Booking cancelled',
  } satisfies Record<BookingAction, string>,
  events: {
    created: 'Booked',
    changed: 'Changed',
    confirmed: 'Confirmed',
    delivered: 'Delivered',
    closed: 'Closed',
    cancelled: 'Cancelled',
    sent_to_kitchen: 'Sent to the kitchen',
    payment_deposit: 'Deposit taken',
    payment_payment: 'Payment taken',
    payment_refund: 'Refund given',
    payment_voided: 'Payment voided',
  } as Record<string, string>,
} as const

/** Plan screen strings for changing a plan after it was made (Stage 2B). */
export const RT = {
  changePlan: 'Change plan',
  changeHelp: 'Numbers changed after the plan was made? Set the new dishes. More is asked from the store; for less, you decide what to keep or send back.',
  applyChange: 'Apply the change',
  applying: 'Working it out…',
  more: 'Needed more',
  less: 'Needed less',
  noDifference: 'No difference to the stock needed.',
  topUp: (no: string) => `Top-up request ${no} sent to the store.`,
  reopened: 'The plan was opened again with the new dishes — lock it again when ready.',
  updated: 'Plan updated.',
  sendBackTitle: 'Send back to store',
  sendBackHelp: 'Only what the kitchen does not need. Anything you keep stays in the kitchen.',
  sendBack: 'Send back',
  sendBackDone: 'Sent back — the store will check it in',
  nothingToSend: 'Set an amount to send back.',
  forBooking: (no: string, customer: string) => `Catering ${no} — ${customer}`,
} as const

/** Booking ↔ kitchen hand-off and save conflicts (pilot fixes, 2026-10-25). */
export const CBT = {
  changedBySomeone: 'Someone else changed this booking — reload to see their changes. Your edits are still here.',
  loadTheirs: 'Load their changes',
  kitchenCancelled: 'The kitchen cancelled its plan. Send the booking again when it is ready.',
  // Batch 6: a save over the kitchen's own change to the plan.
  kitchenEditTitle: 'Save over the kitchen\'s change?',
  kitchenChanged: (what: string) => `The kitchen changed this plan: ${what}.`,
  setItBack: (back: boolean, qty: string) => `Saving will set it ${back ? 'back to' : 'to'} ${qty}.`,
  setThemBack: (what: string) => `Saving will set them to the booking's numbers: ${what}.`,
  kitchenEditConfirm: 'Save the booking',
  kitchenEditHint: 'The booking is what the customer ordered. Tell the kitchen if their change still matters.',
} as const

/** Booking form, booking page and list chips (Batch 4 redesign, 2026-10-25). */
export const CXT = {
  // form
  newTitle: 'New booking',
  editTitle: 'Change booking',
  sectionWho: '1 · Who and when',
  sectionMenu: '2 · Menu and price',
  sectionExtras: '3 · Extras — equipment, packaging, discount',
  customer: 'Customer name',
  phone: 'Phone',
  guests: 'Guests',
  moreDetails: 'More details — time, venue, event name, served or packed',
  time: 'Time',
  venue: 'Venue',
  eventName: 'Event name',
  addDishes: 'Add dishes',
  chosen: (dishes: string, portions: string) => `Chosen · ${dishes} · ${portions}`,
  noneChosen: 'No dishes yet — tap a dish above to add it.',
  equalsGuests: '= guests',
  portionsUnit: 'portions',
  guestsOffer: (n: number, guests: number) => `Set ${n} dish${n === 1 ? '' : 'es'} to ${guests} portions`,
  guestsOfferHelp: (old: number) => `These were made for ${old} guests.`,
  guestsOfferDone: 'Portions updated',
  lineNote: 'Note for the kitchen (allergies, spice)',
  customerNote: 'Note for the kitchen (allergies, spice)',
  customerNoteHelp: 'Goes on the kitchen plan and the kitchen screen.',
  guestsDefaultHelp: 'New dishes start at one portion per guest.',
  // quote card
  quote: 'Quote',
  quoteDishes: (dishes: string, portions: string) => `${dishes} · ${portions}`,
  quoteExtras: (n: number) => `Equipment & packaging: ${n} item${n === 1 ? '' : 's'} (not charged)`,
  depositNow: 'Deposit taken now (optional)',
  depositHow: 'How was it paid?',
  depositTooBig: 'The deposit is more than the total.',
  confirmBooking: 'Confirm booking',
  saveEnquiry: 'Save as enquiry',
  saveChanges: 'Save changes',
  savedWithDeposit: (amt: string) => `Booking saved with a ${amt} deposit`,
  depositFailed: (amt: string) =>
    `The booking is saved, but the ${amt} deposit may not have been recorded. Check Payments on the booking before taking it again.`,
  // booking page
  more: 'More',
  steps: {
    enquiry: 'Enquiry',
    confirmed: 'Confirmed',
    kitchen: 'With the kitchen',
    delivered: 'Delivered',
    paid: 'Paid',
    closed: 'Closed',
  },
  stepState: { done: 'Done', now: 'Now', todo: 'Next' },
  progress: 'Progress',
  next: {
    confirm: { title: 'Next: confirm the booking', body: 'It is an enquiry — nothing is held for it yet. Confirm once the customer agrees.' },
    send: { title: 'Next: send it to the kitchen', body: 'The kitchen plans the cooking and asks the store for what it needs.' },
    resend: { title: 'The kitchen cancelled its plan', body: 'Send the booking again when it is ready.' },
    withKitchen: { title: 'The kitchen has it', body: 'Nothing for you to do until the event. After it, mark it delivered.' },
    eventDone: { title: 'Event done? Mark it delivered', body: 'The dishes are locked after this. Then take what is still owed.' },
    collect: { title: '', body: 'Record the payment below, then close the booking.' },
    refund: { title: '', body: 'Record the refund below, then close the booking.' },
    close: { title: 'Paid in full — close the booking', body: 'A closed booking can’t be changed and takes no more payments.' },
    closed: { title: 'All done', body: 'This booking is closed.' },
    cancelled: { title: 'Cancelled', body: 'Nothing more to do.' },
    cancelledRefund: { title: '', body: 'The booking was cancelled. Record the refund below.' },
  },
  storeProgress: (asked: number, sent: number) => `store asked for ${asked} item${asked === 1 ? '' : 's'}, ${sent} sent so far`,
  stillToPay: (amt: string) => `${amt} still to pay`,
  toRefund: (amt: string) => `${amt} to give back`,
  paidRefund: (amt: string) => `${amt} paid — to give back`,
  takePayment: 'Take payment',
  giveRefund: 'Record refund',
  deliverEarlyLabel: (date: string) => `Mark delivered (event ${date})`,
  noAction: 'A manager takes this step.',
  money: 'Money',
  paidLine: 'Paid',
  paidDeposit: (date: string, method: string) => `Paid (deposit ${date}, ${method.toLowerCase()})`,
  stillToPayLabel: 'Still to pay',
  owedBackLabel: 'To give back',
  menu: 'Menu',
  menuSummary: (dishes: string, portions: string) => `${dishes} · ${portions}`,
  customerNoteSent: 'Customer note (sent to the kitchen)',
  kitchenLine: (no: string, status: string) => `Kitchen plan ${no} — ${status}`,
  // list chips
  chip: {
    send: 'Send to kitchen',
    resend: 'Send to kitchen again',
    confirm: 'Confirm or cancel',
    eventPassed: 'Event passed — mark delivered',
    close: 'Ready to close',
    withKitchen: 'With the kitchen',
    due: (amt: string) => `${amt} due`,
    refund: (amt: string) => `${amt} to refund`,
  },
} as const

/** Per-guest pricing (Batch 4 lane perhead, migration 179). */
export const PGT = {
  pricing: 'How is it charged?',
  perHead: 'Per head',
  perDish: 'Per dish',
  pricePerGuest: 'Price per guest',
  /** "× 250 guests = Rs 600,000" beside the price field. */
  timesGuests: (guests: number, total: string) => `× ${guests} guest${guests === 1 ? '' : 's'} = ${total}`,
  needGuestsFirst: 'Enter the number of guests above.',
  addDishHint: 'Tap a dish to add it.',
  dishesIncluded: 'Charged per guest — the dishes below are what the kitchen cooks; they are not charged one by one.',
  /** Quote / money row: "250 guests × Rs 2,400". */
  guestsTimes: (guests: number, price: string) => `${guests} guest${guests === 1 ? '' : 's'} × ${price}`,
  included: (dishes: string, portions: string) => `${dishes} · ${portions} — included`,
  menuIncluded: 'Included in the per-guest price',
  switchTitle: 'Change how this booking is charged?',
  switchBody: (from: string, to: string) => `This changes the total from ${from} to ${to}.`,
  switchConfirm: 'Change and save',
  // Settings
  settingTitle: 'How do you price catering?',
  settingHelp: 'New bookings start this way. You can still switch each booking between per dish and per guest.',
  settingPerDish: 'Per dish',
  settingPerGuest: 'Per guest',
  settingPerDishHelp: 'Each dish is charged: portions × its price.',
  settingPerGuestHelp: 'One price per guest: guests × the price. Dishes are not charged one by one.',
  settingSaved: 'Catering pricing saved',
} as const
